/**
 * Bundle Pack Presets for Featured Product Section
 * Version: 1.0.0
 * Standalone JavaScript - Does NOT depend on BundlePackAddon
 *
 * Manages preset pills, product card quantity controls,
 * quantity meter, price updates, and cart interception.
 */

(function() {
  'use strict';

  class BundlePackPresets {
    constructor(container) {
      this.container = container;
      this.cards = container.querySelectorAll('.bundle-pack-card');
      this.quantityInputs = container.querySelectorAll('.bundle-pack-card__quantity-input');
      this.quantityMeter = this.resolveQuantityMeter();

      // Map<productHandle, { quantity, price, compare_at_price, title }>
      this.selectedVariants = new Map();
      this.activeSize = document.body.dataset.bundleSize || '15';

      // Plafond global du nombre de paquets toutes saveurs confondues (0 = illimité)
      this.maxTotal = parseInt(container.dataset.maxTotal, 10) || 0;

      // Trouver le formulaire produit principal (pas dans un drawer, pas installment Shop Pay)
      const isExcludedForm = (form) => {
        if (!form) return true;
        if (form.closest('cart-drawer')) return true;
        if (form.closest('#CartDrawer')) return true;
        if (form.classList.contains('upsell-drawer-form')) return true;
        if (form.classList.contains('installment')) return true; // form Shop Pay Pay-in-X
        if (!form.querySelector('.product-form__submit')) return true; // pas de bouton ATC dedans
        return false;
      };

      let formCandidate = container.closest('form[action*="/cart/add"]');
      if (formCandidate && isExcludedForm(formCandidate)) formCandidate = null;

      if (!formCandidate) {
        const allForms = document.querySelectorAll('form[action*="/cart/add"]');
        for (const form of allForms) {
          if (!isExcludedForm(form)) {
            formCandidate = form;
            break;
          }
        }
      }

      this.productForm = formCandidate || null;

      // Configuration par défaut des paliers
      this.tiers = [
        { qty: 1, text: '1', price: '' },
        { qty: 2, text: '2', price: '' },
        { qty: 3, text: '3', price: '' },
        { qty: 4, text: '4', price: '' }
      ];

      this.discounts = {
        '1': { type: 'none', value: 0 },
        '2': { type: 'none', value: 0 },
        '3': { type: 'none', value: 0 },
        '4': { type: 'none', value: 0 },
        '5': { type: 'none', value: 0 }
      };

      this.presets = [];
      this.activePresetIndex = 'free';
      this.freePackLabel = 'Mon pack libre';

      this.init();
    }

    resolveQuantityMeter() {
      const baseLinkKey = this.container.dataset.quantityMeterLinkKey;
      const activeSize = this.activeSize || document.body.dataset.bundleSize || '15';
      const allMeters = document.querySelectorAll('[data-quantity-meter-addon]');

      // 1. Tentative : meter avec data-bundle-size-target === activeSize
      for (const meter of allMeters) {
        if (meter.dataset.bundleSizeTarget === activeSize) {
          return meter;
        }
      }

      // 2. Tentative : meter avec linkKey + suffixe taille (ex: "...-bundle-15")
      if (baseLinkKey) {
        const sizedKey = baseLinkKey + '-' + activeSize;
        for (const meter of allMeters) {
          if (meter.dataset.quantityMeterLinkKey === sizedKey) {
            return meter;
          }
        }
        // 3. Fallback legacy : meter avec exactement linkKey de base
        for (const meter of allMeters) {
          if (meter.dataset.quantityMeterLinkKey === baseLinkKey) {
            return meter;
          }
        }
      }

      // 4. Dernier fallback : premier meter trouvé
      return this.container.querySelector('[data-quantity-meter-addon]') || document.querySelector('[data-quantity-meter-addon]');
    }

    init() {
      try {
        this.loadConfiguration();
        this.clearLocalStorage();
        this.bindPresetPills();
        this.bindQuantityControls();
        this.initializeQuantityButtons();
        this.updateQuantityMeter();
        this.updateButtonPrice();
        this.updateSubmitButtonState();
        this.interceptFetchRequests();
        this.initializeScrollIndicator();
        this.initGridLayout();
        this.bindSizeSelector();

        // Écouter quand ButtonPriceManager est prêt
        document.addEventListener('buttonPriceManagerReady', () => {
          if (this.selectedVariants.size > 0) {
            this.updateButtonPrice();
          }
        });
      } catch (e) {
        console.error('[BundlePackPresets] Init error:', e);
      }
    }

    bindSizeSelector() {
      // Synchro initiale avec le selector (au cas où il a été chargé avant nous)
      const initialSize = document.body.dataset.bundleSize;
      if (initialSize && initialSize !== this.activeSize) {
        this.activeSize = initialSize;
        // Re-résoudre le meter pour la taille initiale + recharger sa config
        this.quantityMeter = this.resolveQuantityMeter();
        this.reloadMeterConfig();
      }

      document.addEventListener('bundle:size-changed', (e) => {
        const newSize = e.detail && e.detail.size;
        if (!newSize || newSize === this.activeSize) return;
        this.activeSize = newSize;
        this.resetAllQuantities();
        // Repointer vers le meter qui correspond à la nouvelle taille
        this.quantityMeter = this.resolveQuantityMeter();
        this.reloadMeterConfig();
        this.updateQuantityMeter();
        this.updateButtonPrice();
        this.updateSubmitButtonState();
      });
    }

    // Parse "42,00€" / "42€" / "42.50" → centimes (int)
    parseTierPrice(str) {
      if (!str) return 0;
      const cleaned = String(str).replace(/[^\d.,]/g, '').replace(',', '.');
      const value = parseFloat(cleaned);
      if (isNaN(value)) return 0;
      return Math.round(value * 100);
    }

    // Recharge tiers depuis le quantityMeter actif (utilisé au switch de taille)
    reloadMeterConfig() {
      if (!this.quantityMeter) return;
      const meterItems = this.quantityMeter.querySelectorAll('.quantity-meter-addon-item[data-tier-qty]');
      if (meterItems.length === 0) return;
      this.tiers = Array.from(meterItems).map(item => ({
        qty: parseInt(item.dataset.tierQty, 10) || 0,
        text: '',
        price: ''
      }));
      this.discounts = {};
      meterItems.forEach((item, idx) => {
        const totalPriceCents = this.parseTierPrice(item.dataset.tierTotalPrice);
        if (totalPriceCents > 0) {
          this.discounts[String(idx + 1)] = { type: 'total_price', value: totalPriceCents };
        } else {
          this.discounts[String(idx + 1)] = { type: 'none', value: 0 };
        }
      });
    }

    // ══════════════════════════════════════════
    // Configuration
    // ══════════════════════════════════════════

    loadConfiguration() {
      const config = this.container.dataset;

      if (config.discounts) {
        try {
          this.discounts = JSON.parse(config.discounts);
        } catch (e) {
          // Use defaults
        }
      }

      // Si un quantity meter externe est lié, ses paliers font autorité.
      if (this.quantityMeter) {
        const meterItems = this.quantityMeter.querySelectorAll('.quantity-meter-addon-item[data-tier-qty]');
        if (meterItems.length > 0) {
          this.tiers = Array.from(meterItems).map(item => ({
            qty: parseInt(item.dataset.tierQty, 10) || 0,
            text: '',
            price: ''
          }));
          this.discounts = {};
          meterItems.forEach((item, idx) => {
            const totalPriceCents = this.parseTierPrice(item.dataset.tierTotalPrice);
            if (totalPriceCents > 0) {
              this.discounts[String(idx + 1)] = { type: 'total_price', value: totalPriceCents };
            } else {
              this.discounts[String(idx + 1)] = { type: 'none', value: 0 };
            }
          });
        }
      }

      // Presets
      if (config.presets) {
        try {
          this.presets = JSON.parse(config.presets);
        } catch (e) {
          this.presets = [];
        }
      }

      this.freePackLabel = config.freePackLabel || 'Mon pack libre';

      // Configuration de la remise globale
      this.globalDiscountEnabled = config.globalDiscountEnabled === 'true';
      this.globalDiscountPercentage = parseFloat(config.globalDiscountPercentage) || 0;
      this.globalDiscountColor = config.globalDiscountColor || '#dc2626';

      if (this.globalDiscountEnabled && this.globalDiscountPercentage > 0) {
        this.applyGlobalDiscountToPrices();
        if (this.quantityMeter) {
          this.quantityMeter.classList.add('quantity-meter-addon--global-discount');
        }
      } else {
        if (this.quantityMeter) {
          this.quantityMeter.classList.remove('quantity-meter-addon--global-discount');
        }
      }
    }

    applyGlobalDiscountToPrices() {
      const priceElements = this.container.querySelectorAll('.quantity-meter-addon-item__price[data-original-price]');

      priceElements.forEach(priceEl => {
        const originalPriceText = priceEl.dataset.originalPrice;
        const priceRegex = /(\d+(?:[.,]\d+)?)\s*([€$£])/;
        const match = originalPriceText.match(priceRegex);

        if (match) {
          const originalPrice = parseFloat(match[1].replace(',', '.'));
          const currency = match[2];
          const suffix = originalPriceText.substring(match.index + match[0].length);
          const discountedPrice = originalPrice * (1 - this.globalDiscountPercentage / 100);

          let formattedDiscountedPrice;
          if (originalPriceText.includes(',') || originalPriceText.includes('.')) {
            formattedDiscountedPrice = discountedPrice.toFixed(2).replace('.', ',');
          } else {
            formattedDiscountedPrice = Math.round(discountedPrice).toString();
          }

          priceEl.innerHTML = `
            <span class="quantity-meter-addon-item__price--original">${originalPriceText}</span>
            <span class="quantity-meter-addon-item__price--discounted" style="color: ${this.globalDiscountColor};">
              ${formattedDiscountedPrice}${currency}${suffix}
            </span>
          `;
        }
      });
    }

    // ══════════════════════════════════════════
    // Preset Pills
    // ══════════════════════════════════════════

    bindPresetPills() {
      const pills = this.container.querySelectorAll('.bundle-presets-pill[data-preset-index]');
      pills.forEach(pill => {
        pill.addEventListener('click', (e) => {
          e.preventDefault();
          const index = pill.dataset.presetIndex;
          this.activatePreset(index);
        });
      });
    }

    activatePreset(index) {
      // 1. Mettre à jour le pill actif
      const pills = this.container.querySelectorAll('.bundle-presets-pill');
      pills.forEach(pill => pill.classList.remove('bundle-presets-pill--active'));
      const targetPill = this.container.querySelector(`[data-preset-index="${index}"]`);
      if (targetPill) targetPill.classList.add('bundle-presets-pill--active');

      // 2. Tracker le preset actif
      this.activePresetIndex = index;

      // 3. Reset toutes les quantités
      this.resetAllQuantities();

      // 4. Si preset numérique, appliquer les quantités
      if (index !== 'free' && this.presets[parseInt(index)]) {
        const preset = this.presets[parseInt(index)];
        this.setCardQuantities(preset.products);
      }

      // 5. Mettre à jour tout l'état downstream
      this.updateQuantityMeter();
      this.updateButtonPrice();
      this.updateSubmitButtonState();
      this.saveToLocalStorage();
    }

    setCardQuantities(products) {
      if (!products || !products.length) return;

      products.forEach((entry) => {
        // Compat ancienne API : entry peut être { variantId, qty } ou { handle, qty }
        const handle = entry.handle || entry.productHandle;
        const variantId = entry.variantId;
        let card;
        if (handle) {
          card = this.container.querySelector(`.bundle-pack-card[data-product-handle="${handle}"]`);
        } else if (variantId) {
          card = this.container.querySelector(`.bundle-pack-card[data-variant-id="${variantId}"]`);
        }
        if (!card) return;

        const input = card.querySelector('.bundle-pack-card__quantity-input');
        if (!input) return;

        const quantity = parseInt(entry.qty) || 0;
        input.value = quantity;

        if (quantity > 0) {
          const cardHandle = card.dataset.productHandle;
          const sizeKey = this.activeSize === '30' ? 'Large' : 'Small';
          const sizePrice = card.dataset['variant' + sizeKey + 'Price'] || card.dataset.variantPrice;
          const sizeCompare = card.dataset['variant' + sizeKey + 'CompareAtPrice'] || card.dataset.variantCompareAtPrice;
          this.selectedVariants.set(cardHandle, {
            quantity: quantity,
            price: parseInt(sizePrice),
            compare_at_price: sizeCompare ? parseInt(sizeCompare) : null,
            title: card.querySelector('.bundle-pack-card__title')?.textContent.trim() || ''
          });
          card.classList.add('bundle-pack-card--selected');
        }

        this.updateQuantityButtons(input);
      });
    }

    resetAllQuantities() {
      this.selectedVariants.clear();

      this.quantityInputs.forEach(input => {
        input.value = 0;
        this.updateQuantityButtons(input);
      });

      this.cards.forEach(card => {
        card.classList.remove('bundle-pack-card--selected');
      });
    }

    checkAndSwitchToFreePack() {
      if (this.activePresetIndex === 'free') return;

      // L'utilisateur a modifié manuellement une quantité → switch vers pack libre
      const pills = this.container.querySelectorAll('.bundle-presets-pill');
      pills.forEach(pill => pill.classList.remove('bundle-presets-pill--active'));
      const freePill = this.container.querySelector('[data-preset-index="free"]');
      if (freePill) freePill.classList.add('bundle-presets-pill--active');
      this.activePresetIndex = 'free';
    }

    // ══════════════════════════════════════════
    // Quantity Controls
    // ══════════════════════════════════════════

    bindQuantityControls() {
      this.quantityInputs.forEach(input => {
        const quantityContainer = input.closest('.bundle-pack-card__quantity-controls');
        if (!quantityContainer) return;

        const plusButton = quantityContainer.querySelector('[name="plus"]');
        const minusButton = quantityContainer.querySelector('[name="minus"]');

        if (plusButton) {
          plusButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.incrementQuantity(input);
          });
        }

        if (minusButton) {
          minusButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.decrementQuantity(input);
          });
        }

        const wrapper = quantityContainer.parentElement;
        const addButton = wrapper && wrapper.querySelector('[data-bundle-add]');
        if (addButton) {
          addButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.incrementQuantity(input);
          });
        }
      });
    }

    // Somme des quantités déjà sélectionnées sur toutes les cartes (toutes saveurs)
    currentTotalQuantity() {
      let total = 0;
      this.selectedVariants.forEach((data) => { total += data.quantity; });
      return total;
    }

    incrementQuantity(input) {
      const currentValue = parseInt(input.value) || 0;
      const max = parseInt(input.dataset.max) || 999;

      // Plafond global : bloque l'ajout si le total atteint déjà la limite
      if (this.maxTotal > 0 && this.currentTotalQuantity() >= this.maxTotal) {
        return;
      }

      if (currentValue < max) {
        const newValue = currentValue + 1;
        input.value = newValue;
        this.handleQuantityChangeDirectly(input, newValue);
      }
    }

    decrementQuantity(input) {
      const currentValue = parseInt(input.value) || 0;

      if (currentValue > 0) {
        const newValue = currentValue - 1;
        input.value = newValue;
        this.handleQuantityChangeDirectly(input, newValue);
      }
    }

    handleQuantityChangeDirectly(input, quantity) {
      const card = input.closest('.bundle-pack-card');
      const handle = input.dataset.productHandle || card.dataset.productHandle;
      const max = parseInt(input.dataset.max) || 999;

      if (quantity < 0) {
        input.value = 0;
        quantity = 0;
      }

      if (quantity > max) {
        input.value = max;
        quantity = max;
      }

      // Plafond global : la nouvelle quantité de CETTE carte ne peut pas faire
      // dépasser maxTotal au total (on borne par ce qu'il reste de disponible).
      if (this.maxTotal > 0) {
        const handleForCap = input.dataset.productHandle || card.dataset.productHandle;
        const existing = this.selectedVariants.get(handleForCap);
        const otherTotal = this.currentTotalQuantity() - (existing ? existing.quantity : 0);
        const remaining = this.maxTotal - otherTotal;
        if (quantity > remaining) {
          quantity = Math.max(0, remaining);
          input.value = quantity;
        }
      }

      if (quantity > 0) {
        const sizeKey = this.activeSize === '30' ? 'Large' : 'Small';
        const sizePrice = card.dataset['variant' + sizeKey + 'Price'] || card.dataset.variantPrice;
        const sizeCompare = card.dataset['variant' + sizeKey + 'CompareAtPrice'] || card.dataset.variantCompareAtPrice;
        this.selectedVariants.set(handle, {
          quantity: quantity,
          price: parseInt(sizePrice),
          compare_at_price: sizeCompare ? parseInt(sizeCompare) : null,
          title: card.querySelector('.bundle-pack-card__title')?.textContent.trim() || ''
        });
        card.classList.add('bundle-pack-card--selected');
      } else {
        this.selectedVariants.delete(handle);
        card.classList.remove('bundle-pack-card--selected');
      }

      // Rafraîchir TOUTES les cartes (et pas seulement celle modifiée) : le plafond
      // global peut désactiver/réactiver le "+" des autres saveurs.
      this.refreshAllQuantityButtons();
      this.checkAndSwitchToFreePack();
      this.updateQuantityMeter();
      this.updateButtonPrice();
      this.updateSubmitButtonState();
      this.saveToLocalStorage();
    }

    // Met à jour l'état des boutons +/- sur toutes les cartes du builder.
    refreshAllQuantityButtons() {
      this.quantityInputs.forEach((input) => this.updateQuantityButtons(input));
    }

    updateQuantityButtons(input) {
      const quantityContainer = input.closest('.bundle-pack-card__quantity-controls');
      if (!quantityContainer) return;

      const plusButton = quantityContainer.querySelector('[name="plus"]');
      const minusButton = quantityContainer.querySelector('[name="minus"]');

      const value = parseInt(input.value) || 0;
      const max = parseInt(input.dataset.max) || 999;

      // Plafond global atteint = on désactive le "+" même si le max par produit
      // n'est pas atteint sur cette carte.
      const globalCapReached = this.maxTotal > 0 && this.currentTotalQuantity() >= this.maxTotal;

      const shouldDisableMinus = value <= 0;
      const shouldDisablePlus = value >= max || globalCapReached;

      if (minusButton) {
        minusButton.disabled = shouldDisableMinus;
        minusButton.classList.toggle('disabled', shouldDisableMinus);
      }
      if (plusButton) {
        plusButton.disabled = shouldDisablePlus;
        plusButton.classList.toggle('disabled', shouldDisablePlus);
      }

      const wrapper = quantityContainer.parentElement;
      const addButton = wrapper && wrapper.querySelector('[data-bundle-add]');
      if (addButton) {
        if (value > 0) {
          addButton.hidden = true;
          quantityContainer.hidden = false;
        } else {
          addButton.hidden = false;
          quantityContainer.hidden = true;
        }
      }
    }

    initializeQuantityButtons() {
      this.quantityInputs.forEach(input => {
        this.updateQuantityButtons(input);
      });
    }

    // ══════════════════════════════════════════
    // Quantity Meter
    // ══════════════════════════════════════════

    updateQuantityMeter() {
      let totalQuantity = 0;
      this.selectedVariants.forEach(data => {
        totalQuantity += data.quantity;
      });

      // Notifier les blocs externes (ex: rewards_unlock) qu'une mise à jour de quantité a eu lieu
      document.dispatchEvent(new CustomEvent('bundle:quantity-changed', {
        detail: { totalQty: totalQuantity }
      }));

      if (!this.quantityMeter) return;

      const items = this.quantityMeter.querySelectorAll('.quantity-meter-addon-item');

      let lastActiveTier = -1;
      for (let i = 0; i < this.tiers.length; i++) {
        if (totalQuantity >= this.tiers[i].qty) lastActiveTier = i;
      }

      for (let i = 0; i < this.tiers.length; i++) {
        const item = items[i];
        if (!item) continue;
        const fill = item.querySelector('[data-bar-fill]');
        const tierQty = this.tiers[i].qty;
        const prevQty = i === 0 ? 0 : this.tiers[i - 1].qty;

        let pct;
        if (totalQuantity >= tierQty) {
          pct = 100;
        } else if (totalQuantity <= prevQty) {
          pct = 0;
        } else {
          pct = ((totalQuantity - prevQty) / (tierQty - prevQty)) * 100;
        }

        const isCurrent = i === lastActiveTier + 1 && pct > 0;
        item.classList.toggle('is-active', i === lastActiveTier);
        item.classList.toggle('is-unlocked', i <= lastActiveTier);
        item.classList.toggle('is-current', isCurrent);
        if (fill) fill.style.width = pct + '%';
        const currentEl = item.querySelector('[data-qm-current]');
        if (currentEl) currentEl.textContent = isCurrent ? totalQuantity + '/' : '';
      }

      this.updateGifts(totalQuantity);
    }

    updateGifts(totalQuantity) {
      const event = new CustomEvent('bundlePackQuantityChanged', {
        detail: {
          totalQuantity: totalQuantity,
          selectedVariants: Array.from(this.selectedVariants.entries())
        }
      });
      document.dispatchEvent(event);
    }

    // ══════════════════════════════════════════
    // Price & Submit Button
    // ══════════════════════════════════════════

    updateButtonPrice() {
      if (!this.selectedVariants) return;

      let totalQuantity = 0;
      this.selectedVariants.forEach((data) => {
        totalQuantity += data.quantity;
      });

      let activeTier = null;
      for (let i = this.tiers.length - 1; i >= 0; i--) {
        if (totalQuantity >= this.tiers[i].qty) {
          activeTier = String(i + 1);
          break;
        }
      }

      let totalPriceBeforeDiscount = 0;
      this.selectedVariants.forEach((data) => {
        totalPriceBeforeDiscount += (data.price * data.quantity);
      });

      let totalCompareAtPrice = 0;
      this.selectedVariants.forEach((data) => {
        const comparePrice = data.compare_at_price || data.price;
        totalCompareAtPrice += (comparePrice * data.quantity);
      });

      let totalPrice = totalPriceBeforeDiscount;
      const discount = this.discounts[activeTier];

      if (discount && discount.type !== 'none') {
        if (discount.type === 'total_price') {
          // Prix total fixé manuellement par Antho pour ce palier
          totalPrice = discount.value;
          // Au-delà du DERNIER palier, le prix forfaitaire ne couvre que la quantité
          // du palier. Chaque sachet supplémentaire est facturé au prix unitaire du
          // dernier palier (remise volume Shopify "5+" = même prix/sachet maintenu).
          // Ex: tier4 = 4 sachets à 115€ -> 28,75€/sachet ; le 5e+ = 28,75€ chacun.
          const lastTierIndex = this.tiers.length - 1;
          if (activeTier === String(lastTierIndex + 1)) {
            const lastTierQty = this.tiers[lastTierIndex].qty || 0;
            if (totalQuantity > lastTierQty && lastTierQty > 0) {
              const tierUnitPrice = discount.value / lastTierQty;
              const extraUnits = totalQuantity - lastTierQty;
              totalPrice = discount.value + (tierUnitPrice * extraUnits);
            }
          }
        } else if (discount.type === 'percentage') {
          const reductionMultiplier = (1 - discount.value / 100);
          let discountedTotal = 0;
          this.selectedVariants.forEach((data) => {
            const unitDiscounted = Math.ceil(data.price * reductionMultiplier);
            discountedTotal += unitDiscounted * data.quantity;
          });
          totalPrice = discountedTotal;
        } else if (discount.type === 'fixed') {
          const discountInCentimes = discount.value * 100;
          totalPrice = totalPriceBeforeDiscount - discountInCentimes;
        }
      }

      totalPrice = Math.round(totalPrice);

      const event = new CustomEvent('bundlePackPrice:change', {
        detail: {
          totalPrice: totalPrice,
          totalPriceBeforeDiscount: totalPriceBeforeDiscount,
          totalCompareAtPrice: totalCompareAtPrice,
          selectedVariants: Array.from(this.selectedVariants.entries()),
          hasSelections: this.selectedVariants.size > 0,
          activeTier: activeTier,
          totalQuantity: totalQuantity,
          discount: discount,
          globalDiscountApplied: false
        }
      });
      document.dispatchEvent(event);
    }

    updateSubmitButtonState() {
      const submitButton = document.querySelector('.product-form__submit');
      if (!submitButton) return;

      const textEl = submitButton.querySelector('.product-form__submit-text');
      const separatorEl = submitButton.querySelector('.product-form__submit-separator');
      const priceEl = submitButton.querySelector('.product-form__submit-price');
      if (!textEl) return;

      const hasSelections = this.selectedVariants.size > 0;

      if (!hasSelections) {
        submitButton.disabled = true;
        submitButton.style.opacity = '0.5';
        submitButton.style.cursor = 'not-allowed';
        textEl.textContent = 'Sélectionnez un produit';
        if (separatorEl) separatorEl.style.display = 'none';
        if (priceEl) priceEl.style.display = 'none';
      } else {
        submitButton.disabled = false;
        submitButton.style.opacity = '';
        submitButton.style.cursor = '';
        textEl.textContent = 'Ajouter au panier';
        if (separatorEl) separatorEl.style.display = '';
        if (priceEl) priceEl.style.display = '';
      }
    }

    // ══════════════════════════════════════════
    // Cart Interception
    // ══════════════════════════════════════════

    interceptFetchRequests() {
      const originalFetch = window.fetch;
      const self = this;

      window.fetch = function(...args) {
        const [url, options] = args;

        if (url && (url.includes('/cart/add') || url.includes('cart_add_url'))) {
          let isFromMainForm = false;

          if (options && options.body instanceof FormData) {
            const formData = options.body;
            const formSectionId = formData.get('section-id');
            const mainFormSectionId = self.productForm ? self.productForm.querySelector('input[name="section-id"]')?.value : null;

            if (mainFormSectionId && formSectionId === mainFormSectionId) {
              isFromMainForm = true;
            }
          }

          // Garde-fou : si le form principal (Pack à composer) est soumis alors que
          // AUCUN sachet n'est sélectionné (selectedVariants désynchronisé), on bloque
          // l'envoi. Sinon le produit conteneur "pack-à-composer" (variante 42€) partirait
          // brut au panier ET au pixel Meta (AddToCart faussé : "pack-à-composer ×1, 42€").
          if (isFromMainForm && self.selectedVariants.size === 0) {
            return Promise.resolve(
              new Response(JSON.stringify({ items: [] }), {
                status: 422,
                headers: { 'Content-Type': 'application/json' }
              })
            );
          }

          const shouldIntercept = isFromMainForm && self.selectedVariants.size > 0;

          if (shouldIntercept) {
            let sellingPlanId = null;
            let formProperties = {};
            if (options && options.body instanceof FormData) {
              sellingPlanId = options.body.get('selling_plan');
              for (const [key, value] of options.body.entries()) {
                const match = key.match(/^properties\[(.+)\]$/);
                if (match && value) {
                  formProperties[match[1]] = value;
                }
              }
            }

            const items = [];
            const activeSize = self.activeSize || document.body.dataset.bundleSize || '15';

            self.selectedVariants.forEach((data, handle) => {
              const card = self.container.querySelector(`.bundle-pack-card[data-product-handle="${handle}"]`);
              if (!card) {
                console.warn('[BundlePackPresets] Card introuvable pour handle', handle);
                return;
              }

              const v15Id = card.dataset.variantSmallId;
              const v30Id = card.dataset.variantLargeId;
              const qty = data.quantity;

              const pushItem = (variantId, quantity) => {
                if (!variantId || quantity <= 0) return;
                const item = { id: parseInt(variantId), quantity: quantity };
                if (sellingPlanId) item.selling_plan = parseInt(sellingPlanId);
                // Properties : on filtre _frequency (info d'abonnement) si AUCUN selling_plan
                // n'est appliqué, sinon un _frequency résiduel du formulaire colle une fréquence
                // sur une ligne achat-unique (visible côté admin).
                var props = {};
                Object.keys(formProperties).forEach(function (k) {
                  if (k === '_frequency' && !sellingPlanId) return; // pas d'abo → pas de fréquence
                  props[k] = formProperties[k];
                });
                if (Object.keys(props).length > 0) item.properties = props;
                items.push(item);
              };

              if (activeSize === '15') {
                pushItem(v15Id, qty);
              } else if (activeSize === '30') {
                pushItem(v30Id, qty);
              } else {
                // Taille inconnue → fallback variant par défaut
                pushItem(card.dataset.variantId, qty);
              }
            });

            // Ajouter le bocal upsell-gift si sélectionné
            if (window.__upsellGift) {
              const giftItem = window.__upsellGift.getSelectedItem();
              if (giftItem) {
                items.push({ id: giftItem.id, quantity: giftItem.quantity });
              }
            }

            const jsonBody = { items: items };

            if (options && options.body instanceof FormData) {
              const formData = options.body;
              const sections = formData.get('sections');
              const sections_url = formData.get('sections_url');
              if (sections) jsonBody.sections = sections;
              if (sections_url) jsonBody.sections_url = sections_url;
            }

            options.body = JSON.stringify(jsonBody);

            if (!options.headers) options.headers = {};
            if (options.headers instanceof Headers) {
              options.headers.set('Content-Type', 'application/json');
              options.headers.set('Accept', 'application/json');
            } else {
              options.headers['Content-Type'] = 'application/json';
              options.headers['Accept'] = 'application/json';
            }
          }

          const fetchPromise = originalFetch.apply(this, args);

          if (isFromMainForm && self.selectedVariants.size > 0) {
            return fetchPromise.then(response => {
              if (response.ok) {
                setTimeout(() => {
                  self.clearLocalStorage();
                  self.reset();
                }, 800);
              }
              return response;
            }).catch(error => {
              console.error('[BundlePackPresets] Erreur:', error);
              throw error;
            });
          }

          return fetchPromise;
        }

        return originalFetch.apply(this, args);
      };
    }

    updateCartUI(cartData) {
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          const cartCount = document.querySelector('cart-notification-button .cart-count-bubble span, #cart-icon-bubble span');
          if (cartCount) {
            cartCount.textContent = cart.item_count;
          }

          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          } else {
            const cartNotification = document.querySelector('cart-notification');
            if (cartNotification && typeof cartNotification.open === 'function') {
              cartNotification.open();
            }
          }
        })
        .catch(error => {
          // Silently fail
        });
    }

    // No-ops — la persistance localStorage a été supprimée.
    saveToLocalStorage() {}
    clearLocalStorage() {}

    // ══════════════════════════════════════════
    // Grid Layout
    // ══════════════════════════════════════════

    initGridLayout() {
      this.isGridLayout = this.container.dataset.gridLayout === 'true';
      if (!this.isGridLayout) return;

      const grid = this.container.querySelector('.bundle-pack-grid');
      if (grid) grid.classList.add('grid-mode');

      const scrollWrapper = this.container.querySelector('.bundle-pack-scroll-wrapper');
      if (scrollWrapper) {
        scrollWrapper.style.maxHeight = 'none';
        scrollWrapper.style.overflow = 'visible';
        const indicator = scrollWrapper.querySelector('.bundle-pack-scroll-indicator');
        if (indicator) indicator.style.display = 'none';
      }
    }

    // ══════════════════════════════════════════
    // Scroll Indicator
    // ══════════════════════════════════════════

    initializeScrollIndicator() {
      const scrollWrapper = this.container.querySelector('.bundle-pack-scroll-wrapper');
      if (!scrollWrapper) return;

      const checkScrollNeeded = () => {
        const grid = scrollWrapper.querySelector('.bundle-pack-grid');
        const contentHeight = grid ? grid.scrollHeight : scrollWrapper.scrollHeight;
        const hasScroll = contentHeight > scrollWrapper.clientHeight + 10;
        if (!hasScroll) {
          scrollWrapper.classList.add('no-scroll');
        } else {
          scrollWrapper.classList.remove('no-scroll');
        }
      };

      const handleScroll = () => {
        const scrollTop = scrollWrapper.scrollTop;
        const scrollHeight = scrollWrapper.scrollHeight;
        const clientHeight = scrollWrapper.clientHeight;
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;

        if (isAtBottom) {
          scrollWrapper.classList.add('scrolled-to-bottom');
        } else {
          scrollWrapper.classList.remove('scrolled-to-bottom');
        }
      };

      checkScrollNeeded();
      handleScroll();

      scrollWrapper.addEventListener('scroll', handleScroll);

      setTimeout(() => {
        checkScrollNeeded();
        handleScroll();
      }, 500);

      window.addEventListener('resize', () => {
        checkScrollNeeded();
        handleScroll();
      });
    }

    // ══════════════════════════════════════════
    // Reset
    // ══════════════════════════════════════════

    reset() {
      this.selectedVariants.clear();

      this.quantityInputs.forEach(input => {
        input.value = 0;
        this.updateQuantityButtons(input);
      });

      this.cards.forEach(card => {
        card.classList.remove('bundle-pack-card--selected');
      });

      // Revenir au pack libre
      const pills = this.container.querySelectorAll('.bundle-presets-pill');
      pills.forEach(pill => pill.classList.remove('bundle-presets-pill--active'));
      const freePill = this.container.querySelector('[data-preset-index="free"]');
      if (freePill) freePill.classList.add('bundle-presets-pill--active');
      this.activePresetIndex = 'free';

      this.updateQuantityMeter();
      this.updateButtonPrice();
      this.updateSubmitButtonState();
    }

    // ══════════════════════════════════════════
    // Public API
    // ══════════════════════════════════════════

    getSelectedVariants() {
      return Array.from(this.selectedVariants.entries());
    }

    getTotalQuantity() {
      let total = 0;
      this.selectedVariants.forEach(data => {
        total += data.quantity;
      });
      return total;
    }

    getTotalPrice() {
      let total = 0;
      this.selectedVariants.forEach(data => {
        total += (data.price * data.quantity);
      });
      return total;
    }
  }

  // Auto-initialize when DOM is ready
  function initializeBundlePackPresets() {
    const containers = document.querySelectorAll('[data-bundle-presets]');
    containers.forEach(container => {
      if (!container.bundlePackPresets) {
        container.bundlePackPresets = new BundlePackPresets(container);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeBundlePackPresets);
  } else {
    initializeBundlePackPresets();
  }

  document.addEventListener('shopify:section:load', initializeBundlePackPresets);

  window.BundlePackPresets = BundlePackPresets;

})();
